import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Dialog, useToast } from "@validereinc/common-components";
import type { EventType } from "@validereinc/domain";
import { EventAdapter, Resources } from "@validereinc/domain";
import React from "react";

export const DeleteEventDialog = ({
  isOpen,
  onClose,
  event,
  onDeleteSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  event: EventType;
  onDeleteSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { isLoading, mutate: deleteEvent } = useMutation({
    mutationFn: async () => {
      await EventAdapter.deleteOne({ id: event.id });
    },
    onSuccess: () => {
      toast.push({
        intent: "success",
        description: `Successfully deleted event "${event.name}".`,
      });

      queryClient.invalidateQueries({
        queryKey: ["events"],
      });
      queryClient.invalidateQueries({
        queryKey: [Resources.WORKFLOW],
      });

      onClose();
      onDeleteSuccess?.();
    },
    onError: (error) => {
      console.error("Error deleting event", error);

      toast.push({
        intent: "error",
        description: "There was an error deleting the event. Please try again.",
      });
    },
  });

  return (
    <Dialog
      title="Delete Event"
      isOpen={isOpen}
      onClose={onClose}
      actionRow={[
        <Button
          key="delete-button"
          onClick={() => {
            deleteEvent();
          }}
          variant="error"
          isLoading={isLoading}
        >
          Delete
        </Button>,
      ]}
    >
      <p>
        Are you sure you want to delete the event <strong>{event.name}</strong>?
      </p>
      <p>
        This action cannot be undone and will remove all associated data
        including form submissions, alerts, and equipment associations.
      </p>
    </Dialog>
  );
};
